/**
 * Creates an interpolation function from an array of data points.
 *
 * @param {Array<{distance: number, value: number}>} dataPoints - Sorted array of data points
 *   where `distance` is the position along the geometry and `value` is the value at that position.
 * @returns {function(number): number} A function that takes a distance and returns the interpolated value.
 */
export function createLinearInterpolator(dataPoints) {
  if (dataPoints.length === 0) {
    return () => 0;
  }
  if (dataPoints.length === 1) {
    return () => dataPoints[0].value;
  }

  return function interpolate(distance) {
    if (distance <= dataPoints[0].distance) {
      return dataPoints[0].value;
    }
    if (distance >= dataPoints[dataPoints.length - 1].distance) {
      return dataPoints[dataPoints.length - 1].value;
    }

    for (let i = 0; i < dataPoints.length - 1; i++) {
      const p0 = dataPoints[i];
      const p1 = dataPoints[i + 1];
      if (distance >= p0.distance && distance <= p1.distance) {
        const range = p1.distance - p0.distance;
        if (range === 0) return p0.value;
        const t = (distance - p0.distance) / range;
        return p0.value + t * (p1.value - p0.value);
      }
    }

    return 0;
  };
}
